import React from "react";
import { useEnhancedInactivity } from "../../context/EnhancedInactivityContext";
import { 
  Clock, 
  AlertTriangle, 
  Shield, 
  LogOut, 
  RefreshCw,
  Settings,
  Bell
} from "lucide-react";

const EnhancedTimeoutModal = ({ userRole = 'agent' }) => {
  const { 
    isModalVisible, 
    countdown, 
    warningLevel, 
    isLoading,
    handleStayLoggedIn, 
    handleLogout,
    getSessionStatus 
  } = useEnhancedInactivity();

  const isAdmin = userRole === 'admin';
  const sessionStatus = getSessionStatus();

  // Progressive warning component
  const WarningNotification = () => {
    if (!warningLevel || warningLevel === 'critical') return null;

    const getWarningConfig = () => {
      switch (warningLevel) {
        case 'subtle':
          return {
            icon: Bell,
            title: 'Session Active',
            message: 'Your session is still active. No action needed.',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            textColor: 'text-blue-800',
            iconColor: 'text-blue-600'
          };
        case 'prominent':
          return {
            icon: AlertTriangle,
            title: 'Session Expiring Soon',
            message: 'You\'ll be logged out in a few minutes due to inactivity.',
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200',
            textColor: 'text-yellow-800',
            iconColor: 'text-yellow-600'
          };
        default:
          return null;
      }
    };

    const config = getWarningConfig();
    if (!config) return null;

    const IconComponent = config.icon;

    return (
      <div className={`fixed top-4 right-4 z-[9999] max-w-sm p-4 rounded-lg border ${config.bgColor} ${config.borderColor} shadow-lg transition-all duration-300`}>
        <div className="flex items-start space-x-3">
          <IconComponent className={`w-5 h-5 mt-0.5 ${config.iconColor}`} />
          <div className="flex-1">
            <h4 className={`font-medium ${config.textColor}`}>{config.title}</h4>
            <p className={`text-sm ${config.textColor} mt-1`}>{config.message}</p>
          </div>
        </div>
      </div>
    );
  };

  // Main timeout modal
  if (!isModalVisible) {
    return <WarningNotification />;
  }

  const getModalConfig = () => {
    if (isAdmin) {
      return {
        title: 'Admin Session Timeout',
        description: 'Your admin session is about to expire due to inactivity.',
        countdownColor: 'text-red-500',
        buttonStyle: 'bg-blue-700 hover:bg-blue-800',
        icon: Shield
      };
    }
    return {
      title: 'Session Timeout',
      description: 'You\'ve been inactive for a while and will be logged out soon.',
      countdownColor: 'text-orange-500',
      buttonStyle: 'bg-blue-600 hover:bg-blue-700',
      icon: Clock
    };
  };

  const modalConfig = getModalConfig();
  const IconComponent = modalConfig.icon;

  return (
    <>
      <WarningNotification />
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-xl p-8 shadow-2xl min-w-[400px] max-w-md mx-4 text-center border border-gray-200 animate-in fade-in-0 zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 rounded-full bg-red-50">
              <IconComponent className="w-8 h-8 text-red-600" />
            </div>
          </div>
          
          <h2 className="mb-3 text-2xl font-bold text-gray-900">
            {modalConfig.title}
          </h2>
          
          <p className="mb-6 text-base text-gray-700 leading-relaxed">
            {modalConfig.description}
          </p>
          
          {/* Countdown Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center justify-center space-x-2">
              <Clock className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600">You'll be logged out in</span>
            </div>
            <div className={`text-3xl font-bold ${modalConfig.countdownColor} mt-2`}>
              {countdown}
            </div>
            <div className="text-sm text-gray-500 mt-1">seconds</div>
          </div>
          
          {/* Session Status */}
          {sessionStatus.status === 'active' && (
            <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-center space-x-2 text-green-700">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Session Active</span>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleStayLoggedIn}
              disabled={isLoading}
              className={`px-6 py-3 text-base font-semibold text-white rounded-lg shadow transition-all duration-200 ${modalConfig.buttonStyle} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2`}
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  <span>Stay Logged In</span>
                </>
              )}
            </button>
            
            <button
              onClick={handleLogout}
              disabled={isLoading}
              className="px-6 py-3 text-base font-semibold text-gray-800 bg-white rounded-lg border border-gray-300 shadow transition-all duration-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
          
          {/* Additional Info */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              {isAdmin 
                ? 'Admin sessions require periodic activity for security.' 
                : 'Your session will be extended when you interact with the page.'
              }
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default EnhancedTimeoutModal;
